import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useRoom } from '@/hooks/useRoom';
import * as session from '@/net/session';
import { summarizeProbe, useRoomProbe } from './useRoomProbe';
import { cn } from '@/lib/utils';

export interface RejoinMemo {
  code: string;
  name: string;
  color: string;
}

/**
 * The networking module exposes `recoverableJoin()` once a guest has joined
 * a room; until it lands this returns null so the card simply stays hidden.
 */
export function readRejoinMemo(): RejoinMemo | null {
  const s = session as unknown as { recoverableJoin?: () => RejoinMemo | null };
  return typeof s.recoverableJoin === 'function'
    ? (s.recoverableJoin() ?? null)
    : null;
}

/** Offer a guest the room they were last in. */
export function RejoinCard({ memo }: { memo: RejoinMemo }) {
  const navigate = useNavigate();
  const room = useRoom();
  const probe = useRoomProbe(memo.code);
  const status = summarizeProbe(probe);
  return (
    <Card className="border-accent-2/60 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-2">
            You were in a room
          </p>
          <h2 className="mt-1 font-display text-xl tracking-wide">
            Room <span className="font-mono text-accent-2">{memo.code}</span>
          </h2>
          <p className="mt-1 text-sm text-muted">
            Playing as {memo.name}.{' '}
            {status.gone
              ? 'This room is over, so there is nothing to rejoin.'
              : 'Rejoin to pick up where you left off.'}
          </p>
          <p
            className={cn(
              'mt-1 text-xs font-semibold uppercase tracking-wider',
              status.tone === 'ok' && 'text-accent-3',
              status.tone === 'warn' && 'text-warn',
              status.tone === 'bad' && 'text-danger',
              status.tone === 'muted' && 'text-muted'
            )}
            role="status"
          >
            {status.label}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => room.leave()}>
            Forget
          </Button>
          <Button
            size="lg"
            disabled={status.gone}
            onClick={() =>
              navigate({ to: '/join/$code', params: { code: memo.code } })
            }
          >
            Rejoin room {memo.code}
          </Button>
        </div>
      </div>
    </Card>
  );
}
